import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Badge,
  Button,
  Callout,
  ChartTrendingUpIcon,
  ChevronDownIcon,
  ConfirmationDialog,
  CountChip,
  DownloadIcon,
  EyeIcon,
  Label,
  LoadingSpinner,
  PencilIcon,
  PlusIcon,
  SaveIcon,
  Switch,
  TableIcon,
  TextInput,
  TrashIcon,
} from '../../components/ui';
import { Modal, ModalContent, ModalFooter, ModalHeader, ModalTitle } from '../../components/ui/Modal';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useTopbar } from '../../contexts/TopbarContext';
import { cn } from '../../lib/utils';
import {
  createEFormSavedReport,
  deleteEFormSavedReport,
  drilldownEFormReport,
  queryEFormReport,
  updateEFormSavedReport,
} from '../../services/mutations';
import ReportPivotBuilder from './ReportPivotBuilder';
import { EFormSidebarPlacementControls } from './EFormSidebarPlacementControls';
import { pivotReport } from './reportPivot';
import {
  defaultDraft,
  fromQuery,
  toQuery,
  type ReportPivotDraft,
} from './reportPivotModel';
import {
  normalizeReportSidebarPlacements,
  type EFormSidebarPlacement,
} from './sidebarPlacements';
import {
  type EFormReportCatalog,
  type EFormReportQueryResult,
  type EFormSavedReport,
  useEFormDefinition,
  useEFormReportCatalog,
  useEFormSavedReports,
} from './useData';

const ReportsPage: React.FC<{
  embedded?: boolean;
  definitionIdOverride?: number;
  savedIdOverride?: number | 'new';
}> = ({ embedded = false, definitionIdOverride, savedIdOverride }) => {
  const { definitionId: defParam } = useParams<{ definitionId: string }>();
  const definitionId = definitionIdOverride ?? (defParam ? parseInt(defParam, 10) : NaN);
  const { colors } = useTheme();
  const { can } = useAuth();
  const { showSuccess, showError } = useNotification();
  const { setTitleOverride } = useTopbar();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const canEdit = can('e_form_submissions', 'edit');
  const canDelete = can('e_form_submissions', 'delete');

  const def = useEFormDefinition(Number.isInteger(definitionId) ? definitionId : 0);
  const catalog = useEFormReportCatalog(Number.isInteger(definitionId) ? definitionId : null);
  const saved = useEFormSavedReports(Number.isInteger(definitionId) ? definitionId : null);

  const [draft, setDraft] = useState<ReportPivotDraft>(() => defaultDraft());
  const [approvedOnly, setApprovedOnly] = useState(false);
  const [result, setResult] = useState<EFormReportQueryResult | null>(null);
  const [running, setRunning] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [activeSavedId, setActiveSavedId] = useState<number | null>(null);
  const [deleteSavedId, setDeleteSavedId] = useState<number | null>(null);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [dialogName, setDialogName] = useState('');
  const [saving, setSaving] = useState(false);
  const [sidebarPlacements, setSidebarPlacements] = useState<EFormSidebarPlacement[]>([]);
  const loadedSavedParam = useRef<string | null>(null);

  useEffect(() => {
    if (embedded) return;
    const name = def.data?.name ?? catalog.data?.name ?? 'Reports';
    setTitleOverride(`${name} · Reports`);
    return () => setTitleOverride(undefined);
  }, [def.data?.name, catalog.data?.name, embedded, setTitleOverride]);

  const queryPayload = useMemo(
    () => toQuery(draft, { approvedOnly }),
    [draft, approvedOnly],
  );

  const runQuery = useCallback(async () => {
    if (!Number.isInteger(definitionId)) return;
    setRunning(true);
    try {
      const data = await queryEFormReport(definitionId, queryPayload);
      setResult(data);
    } catch (e: any) {
      showError(e?.response?.data?.message || e?.message || 'Report query failed');
    } finally {
      setRunning(false);
    }
  }, [definitionId, queryPayload, showError]);

  useEffect(() => {
    if (!catalog.data) return;
    const timer = window.setTimeout(() => void runQuery(), 350);
    return () => window.clearTimeout(timer);
  }, [catalog.data, runQuery]);

  const display = useMemo(
    () => (result ? pivotReport(result, { showTotals: draft.showTotals }) : null),
    [result, draft.showTotals],
  );

  const loadSaved = useCallback((row: EFormSavedReport) => {
    const q = row.queryJson || {};
    setDraft(fromQuery(q, row.chartType));
    setApprovedOnly(Boolean(q.approvedOnly));
    setActiveSavedId(row.id);
    setSaveName(row.name);
    setSidebarPlacements(normalizeReportSidebarPlacements(row.sidebarPlacements));
  }, []);

  const handlePlacementsChange = useCallback(
    async (next: EFormSidebarPlacement[]) => {
      setSidebarPlacements(next);
      if (!Number.isInteger(definitionId) || activeSavedId == null) return;
      try {
        await updateEFormSavedReport(definitionId, activeSavedId, {
          sidebarPlacements: next,
        });
        await saved.mutate();
      } catch (e: any) {
        showError(e?.response?.data?.message || e?.message || 'Could not update sidebar placement');
      }
    },
    [definitionId, activeSavedId, saved, showError],
  );

  useEffect(() => {
    const savedParam =
      savedIdOverride && savedIdOverride !== 'new' ? String(savedIdOverride) : searchParams.get('saved');
    if (!savedParam || !saved.data?.length) return;
    if (loadedSavedParam.current === savedParam) return;
    const id = parseInt(savedParam, 10);
    if (!Number.isInteger(id)) return;
    const row = saved.data.find((r) => r.id === id);
    if (!row) return;
    loadedSavedParam.current = savedParam;
    loadSaved(row);
    if (!embedded && searchParams.get('saved')) {
      const next = new URLSearchParams(searchParams);
      next.delete('saved');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, saved.data, loadSaved, setSearchParams, savedIdOverride, embedded]);

  const openSaveDialog = () => {
    setDialogName(saveName || '');
    setSaveDialogOpen(true);
  };

  const handleSave = async () => {
    if (!Number.isInteger(definitionId) || !dialogName.trim()) {
      showError('Enter a report name');
      return;
    }
    setSaving(true);
    try {
      const name = dialogName.trim();
      if (activeSavedId) {
        await updateEFormSavedReport(definitionId, activeSavedId, {
          name,
          queryJson: queryPayload,
          chartType: draft.chartType,
          sidebarPlacements,
        });
        showSuccess('Report updated');
        setSaveName(name);
        setSaveDialogOpen(false);
        await saved.mutate();
      } else {
        const created = await createEFormSavedReport(definitionId, {
          name,
          queryJson: queryPayload,
          chartType: draft.chartType,
          sidebarPlacements,
          isShared: true,
        });
        setActiveSavedId(created.id);
        setSaveName(name);
        showSuccess('Report saved');
        setSaveDialogOpen(false);
        await saved.mutate();
        navigate(
          embedded
            ? `/e-forms/builder/${definitionId}?section=reports&report=${created.id}`
            : `/e-forms/${definitionId}/reports/${created.id}`,
        );
        return;
      }
    } catch (e: any) {
      showError(e?.response?.data?.message || e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSaved = async () => {
    if (deleteSavedId == null) return;
    try {
      await deleteEFormSavedReport(definitionId, deleteSavedId);
      if (activeSavedId === deleteSavedId) {
        setActiveSavedId(null);
        setSaveName('');
        setSidebarPlacements([]);
      }
      await saved.mutate();
      showSuccess('Report deleted');
    } catch (e: any) {
      showError(e?.response?.data?.message || e?.message || 'Delete failed');
    } finally {
      setDeleteSavedId(null);
    }
  };

  const handleDrilldown = async (
    row: Record<string, unknown>,
    colEquals?: Record<string, unknown>,
  ) => {
    if (row.__total) return;
    try {
      const dimEquals: Record<string, unknown> = { ...(colEquals || {}) };
      (result?.columns || [])
        .filter((c) => c.kind === 'dimension' && c.axis !== 'column')
        .forEach((c) => {
          dimEquals[c.key] = row[c.key];
        });
      const data = await drilldownEFormReport(definitionId, {
        ...queryPayload,
        dimensionEquals: dimEquals,
      });
      if (!data.ids.length) {
        showError('No matching submissions');
        return;
      }
      navigate(`/e-forms/${definitionId}/submissions?ids=${data.ids.join(',')}`);
    } catch (e: any) {
      showError(e?.response?.data?.message || e?.message || 'Drill-down failed');
    }
  };

  const handleExport = async () => {
    if (!display?.rows.length) return;
    const { exportToExcel } = await import('../../components/exports');
    await exportToExcel({
      sortedData: display.rows,
      tableColumns: display.columns.map((c) => ({
        key: c.key,
        header: c.group ? `${c.group} · ${c.label}` : c.label,
      })),
      entityName: def.data?.name ?? 'E-Form report',
      exportFileName: `${def.data?.code ?? 'eform'}-report`,
    });
  };

  if (!Number.isInteger(definitionId)) {
    return <div className="p-6">Invalid form</div>;
  }

  if (catalog.isLoading || def.isLoading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <LoadingSpinner size="large" message="Loading report catalog…" />
      </div>
    );
  }

  if (catalog.isError || !catalog.data) {
    return (
      <div className={cn('p-6', colors.text)}>
        <Callout variant="danger" title="Could not load reportable fields">
          <Link to={`/e-forms/${definitionId}/submissions`}>Back to submissions</Link>
        </Callout>
      </div>
    );
  }

  const cat = catalog.data as EFormReportCatalog;

  return (
    <div className={cn('flex h-full min-h-0 flex-col gap-3', colors.text)}>
      {embedded ? (
        <div className={cn('rounded-xl border px-4 py-3 text-sm', colors.border, colors.surface)}>
          <p className="font-medium">Build a table of submissions</p>
          <p className={cn('mt-1', colors.textMuted)}>
            Click a field on the left to add it as a table column. Leave “Pivot across” empty unless you want
            a spreadsheet-style pivot. Save when the table looks right, then open it from the menu or the central dashboard.
          </p>
        </div>
      ) : null}
      <div className="flex shrink-0 flex-col gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-900">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0 flex-1 space-y-2.5">
            <div className="flex flex-wrap items-center gap-2">
              {embedded ? null : <h1 className="text-xl font-semibold tracking-tight">{cat.name}</h1>}
              {embedded ? <h2 className="text-base font-semibold tracking-tight">{activeSavedId ? saveName || 'Saved report' : 'New table report'}</h2> : null}
              {activeSavedId ? <Badge variant="secondary">Saved view</Badge> : null}
            </div>
            <div className="flex max-w-3xl flex-wrap items-center gap-2">
              <SavedReportsMenu
                items={saved.data || []}
                activeId={activeSavedId}
                definitionId={definitionId}
                canDelete={canDelete}
                onEdit={loadSaved}
                onDelete={(id) => setDeleteSavedId(id)}
              />
              {canEdit ? (
                <>
                  <button
                    type="button"
                    onClick={openSaveDialog}
                    className={cn(
                      'inline-flex h-10 items-center gap-1.5 rounded-xl border px-3.5 text-sm font-medium',
                      colors.border,
                      colors.text,
                      colors.interactiveHover,
                    )}
                  >
                    <SaveIcon className="h-4 w-4" />
                    {activeSavedId ? 'Update saved' : 'Save report'}
                  </button>
                  {activeSavedId ? (
                    <>
                      <Link
                        to={`/e-forms/${definitionId}/reports/${activeSavedId}`}
                        className={cn(
                          'inline-flex h-10 items-center gap-1.5 rounded-xl px-2.5 text-sm font-medium',
                          colors.text,
                          colors.interactiveHover,
                        )}
                      >
                        <EyeIcon className="h-4 w-4" />
                        Open view
                      </Link>
                      <button
                        type="button"
                        onClick={() => {
                          setActiveSavedId(null);
                          setSaveName('');
                          setSidebarPlacements([]);
                        }}
                        className={cn(
                          'inline-flex h-10 items-center gap-1.5 rounded-xl px-2.5 text-sm font-medium',
                          colors.text,
                          colors.interactiveHover,
                        )}
                      >
                        <PlusIcon className="h-4 w-4" />
                        New report
                      </button>
                    </>
                  ) : null}
                  <EFormSidebarPlacementControls
                    variant="toolbar"
                    placements={sidebarPlacements}
                    onChange={(next) => void handlePlacementsChange(next)}
                    itemName={saveName || dialogName || 'Report'}
                    defaultHidden
                    dragToken="eform-report-shortcut"
                  />
                </>
              ) : null}
            </div>
          </div>
          <div className="flex h-10 shrink-0 flex-wrap items-center gap-2">
            {!embedded && searchParams.get('studio') === '1' ? (
              <Link
                to={`/e-forms/builder/${definitionId}?section=reports`}
                className={cn(
                  'inline-flex h-10 items-center gap-1.5 rounded-xl border px-3.5 text-sm font-medium',
                  colors.border,
                  colors.text,
                  colors.interactiveHover,
                )}
              >
                Back to Studio
              </Link>
            ) : null}
            <Link
              to={`/e-forms/${definitionId}/submissions`}
              className={cn(
                'inline-flex h-10 items-center gap-1.5 rounded-xl border px-3.5 text-sm font-medium',
                colors.border,
                colors.text,
                colors.interactiveHover,
              )}
            >
              <TableIcon className="h-4 w-4" />
              Submissions
            </Link>
            <button
              type="button"
              disabled={!display?.rows.length}
              onClick={() => void handleExport()}
              className={cn(
                'inline-flex h-10 items-center gap-1.5 rounded-xl border px-3.5 text-sm font-medium disabled:opacity-50',
                colors.border,
                colors.text,
                colors.interactiveHover,
              )}
            >
              <DownloadIcon className="h-4 w-4" />
              Export Excel
            </button>
            <button
              type="button"
              disabled={running}
              onClick={() => void runQuery()}
              className={cn(
                'inline-flex h-10 items-center gap-1.5 rounded-xl px-3.5 text-sm font-medium text-white shadow-sm disabled:opacity-60',
                colors.primary,
              )}
            >
              <ChartTrendingUpIcon className={cn('h-4 w-4', running && 'animate-pulse')} />
              {running ? 'Running…' : 'Run report'}
            </button>
          </div>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col">
        <ReportPivotBuilder
          catalog={cat}
          state={draft}
          onChange={setDraft}
          result={result}
          running={running}
          onCellClick={(row, colEquals) => void handleDrilldown(row, colEquals)}
          extraToolbar={
            <Switch
              size="sm"
              checked={approvedOnly}
              onChange={(e) => setApprovedOnly(e.target.checked)}
              label="Approved only"
            />
          }
        />
      </div>

      <Modal
        isOpen={saveDialogOpen}
        onClose={() => {
          if (!saving) setSaveDialogOpen(false);
        }}
        size="md"
      >
        <ModalHeader>
          <ModalTitle>{activeSavedId ? 'Update saved report' : 'Save report'}</ModalTitle>
        </ModalHeader>
        <ModalContent className="space-y-4">
          <div>
            <Label htmlFor="eform-save-report-name">Report name</Label>
            <TextInput
              id="eform-save-report-name"
              value={dialogName}
              onChange={(e) => setDialogName(e.target.value)}
              placeholder="e.g. Monthly submissions by vessel"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  void handleSave();
                }
              }}
            />
          </div>
          <EFormSidebarPlacementControls
            placements={sidebarPlacements}
            onChange={setSidebarPlacements}
            itemName={dialogName || saveName || 'Report'}
            defaultHidden
            defaultExpanded
            dragToken="eform-report-shortcut"
          />
        </ModalContent>
        <ModalFooter>
          <Button
            variant="secondary"
            onClick={() => setSaveDialogOpen(false)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button onClick={() => void handleSave()} loading={saving} disabled={saving}>
            {activeSavedId ? 'Update' : 'Save'}
          </Button>
        </ModalFooter>
      </Modal>

      <ConfirmationDialog
        isOpen={deleteSavedId != null}
        onClose={() => setDeleteSavedId(null)}
        onConfirm={() => void handleDeleteSaved()}
        title="Delete saved report"
        message="This saved pivot will be removed."
        confirmText="Delete"
        confirmVariant="danger"
      />
    </div>
  );
};

function SavedReportsMenu({
  items,
  activeId,
  definitionId,
  canDelete,
  onEdit,
  onDelete,
}: {
  items: EFormSavedReport[];
  activeId: number | null;
  definitionId: number;
  canDelete: boolean;
  onEdit: (row: EFormSavedReport) => void;
  onDelete: (id: number) => void;
}) {
  const { colors } = useTheme();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const active = items.find((r) => r.id === activeId);
  const label = active?.name || 'Saved reports';

  useEffect(() => {
    if (!open) return;
    const onDoc = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc, true);
    return () => document.removeEventListener('mousedown', onDoc, true);
  }, [open]);

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        className={cn(
          'inline-flex h-10 min-w-[140px] items-center justify-between gap-2 rounded-xl border px-3.5 text-sm font-medium',
          colors.border,
          colors.text,
          colors.interactiveHover,
        )}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <span className="flex min-w-0 items-center gap-1.5">
          <ChartTrendingUpIcon className="h-4 w-4 shrink-0" />
          <span className="truncate">{label}</span>
        </span>
        <span className="flex items-center gap-1">
          <CountChip>{items.length}</CountChip>
          <ChevronDownIcon className={cn('h-4 w-4 transition-transform', open && 'rotate-180')} />
        </span>
      </button>
      {open ? (
        <div
          role="listbox"
          className="absolute left-0 z-30 mt-1 w-80 rounded-lg border border-slate-200 bg-white p-1 shadow-lg dark:border-slate-700 dark:bg-slate-900"
        >
          {items.length === 0 ? (
            <p className={cn('px-3 py-2 text-xs', colors.textMuted)}>No saved reports yet.</p>
          ) : (
            <ul className="max-h-64 space-y-0.5 overflow-y-auto">
              {items.map((row) => (
                <li key={row.id} className="flex items-center gap-1 rounded-md px-1">
                  <Link
                    to={`/e-forms/${definitionId}/reports/${row.id}`}
                    role="option"
                    aria-selected={activeId === row.id}
                    className={cn(
                      'min-w-0 flex-1 truncate rounded-md px-2 py-1.5 text-left text-sm hover:bg-slate-100 dark:hover:bg-slate-800',
                      activeId === row.id && 'bg-blue-50 font-medium text-blue-700 dark:bg-blue-950/40 dark:text-blue-300',
                    )}
                    onClick={() => setOpen(false)}
                  >
                    {row.name}
                  </Link>
                  <button
                    type="button"
                    title="Edit in builder"
                    aria-label={`Edit ${row.name}`}
                    className={cn(
                      'shrink-0 rounded-lg p-1.5 transition-colors',
                      colors.textMuted,
                      colors.interactiveHover,
                    )}
                    onClick={() => {
                      onEdit(row);
                      setOpen(false);
                    }}
                  >
                    <PencilIcon className="h-4 w-4" />
                  </button>
                  {canDelete ? (
                    <button
                      type="button"
                      title="Delete report"
                      aria-label={`Delete ${row.name}`}
                      className={cn(
                        'shrink-0 rounded-lg p-1.5 transition-colors text-red-600 dark:text-red-400',
                        colors.interactiveHover,
                      )}
                      onClick={() => {
                        setOpen(false);
                        onDelete(row.id);
                      }}
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
}

export default ReportsPage;
